import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { AlertCircle, ArrowLeft, Eye, FileX, RefreshCw, Search } from "lucide-react";
import {
  usePedidosPaginated,
  type PedidosFiltroParams,
  type PedidosSort,
} from "@/hooks/usePedidos";
import { useUserNamesByIds } from "@/hooks/useUserNamesByIds";
import {
  getStatusListagemLabel,
  getStatusListagemBadgeClass,
  formatDataRenovacaoListagem,
} from "@/lib/pedidosCiclo";
import { CheckCircle } from "lucide-react";
import { listagemThLeft, listagemThCenter, listagemTdLeft, listagemTdCenter } from "@/components/listagem/listagemTableClasses";
import { cn } from "@/lib/utils";
import { CriarPedidoRenovacaoDialog } from "@/components/CriarPedidoRenovacaoDialog";
import { DetalhesPedidoDialog } from "@/components/DetalhesPedidoDialog";

export function PedidosNaoRenovados() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [searchTermInput, setSearchTermInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sort] = useState<PedidosSort>({ field: "data_pedido", direction: "desc" });
  const [pedidoReabrirId, setPedidoReabrirId] = useState<number | null>(null);
  const [pedidoDetalhesId, setPedidoDetalhesId] = useState<number | null>(null);

  const filtrosHook = useMemo<PedidosFiltroParams>(
    () => ({
      listagem: "nao_renovados",
      status: "todos",
      recebimento: "todos",
      tipoNegocio: "todos",
      periodo: { dataInicio: undefined, dataFim: undefined },
    }),
    []
  );

  useEffect(() => {
    if (searchTermInput === "") {
      setSearchTerm("");
      return;
    }
    const t = setTimeout(() => setSearchTerm(searchTermInput), 700);
    return () => clearTimeout(t);
  }, [searchTermInput]);

  const { data: pedidosResult, isLoading, error } = usePedidosPaginated(
    currentPage,
    itemsPerPage,
    searchTerm,
    sort,
    filtrosHook
  );
  const pedidos = pedidosResult?.data || [];
  const totalPedidos = pedidosResult?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalPedidos / itemsPerPage));

  const vendedorIds = useMemo(
    () =>
      Array.from(
        new Set(
          pedidos
            .map((p) => p.vendedor_id || p.orcamento?.vendedor_id)
            .filter(Boolean) as string[]
        )
      ),
    [pedidos]
  );
  const { data: userNamesMap } = useUserNamesByIds(vendedorIds);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 text-red-600">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        Erro ao carregar: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Não renovados</h2>
          <p className="text-gray-600">
            Pedidos cujo ciclo de seguro encerrou sem renovação — separados da listagem principal.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => window.dispatchEvent(new CustomEvent("mudar_secao", { detail: "pedidos" }))}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar aos pedidos
        </Button>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <FileX className="h-5 w-5 text-red-600" />
              Arquivo ({totalPedidos})
            </CardTitle>
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
              <Input
                className="pl-8 h-9"
                placeholder="Buscar cliente ou número..."
                value={searchTermInput}
                onChange={(e) => {
                  setSearchTermInput(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b text-gray-600">
                  <th className={cn(listagemThCenter, "p-3")}>Pedido</th>
                  <th className={cn(listagemThLeft, "p-3")}>Cliente</th>
                  <th className={cn(listagemThCenter, "p-3")}>Status</th>
                  <th className={cn(listagemThCenter, "p-3")}>Data</th>
                  <th className={cn(listagemThLeft, "p-3")}>Motivo</th>
                  <th className={cn(listagemThLeft, "p-3")}>Criado por</th>
                  <th className={cn(listagemThCenter, "p-3")}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((pedido) => {
                  const criadoPor =
                    (pedido.vendedor_id || pedido.orcamento?.vendedor_id)
                      ? userNamesMap?.[pedido.vendedor_id ?? ""] ||
                        userNamesMap?.[pedido.orcamento?.vendedor_id ?? ""] ||
                        "—"
                      : "—";
                  return (
                    <tr key={pedido.id} className="border-b hover:bg-gray-50">
                      <td className={cn(listagemTdCenter, "p-3 font-medium")}>#{String(pedido.id).padStart(6, "0")}</td>
                      <td className={cn(listagemTdLeft, "p-3")}>{pedido.cliente?.nome || `Cliente ${pedido.cliente_id}`}</td>
                      <td className={cn(listagemTdCenter, "p-3")}>
                        <div className="flex justify-center">
                          <Badge className={getStatusListagemBadgeClass(pedido.tipo_negocio, pedido.numero_ciclo)}>
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle className="h-3.5 w-3.5" />
                              {getStatusListagemLabel(pedido.tipo_negocio, pedido.numero_ciclo)}
                            </span>
                          </Badge>
                        </div>
                      </td>
                      <td className={cn(listagemTdCenter, "p-3 text-red-700 font-medium")}>
                        {formatDataRenovacaoListagem(pedido.data_nao_renovado)}
                      </td>
                      <td className={cn(listagemTdLeft, "p-3 text-gray-600 max-w-xs truncate")} title={pedido.motivo_nao_renovacao || ""}>
                        {pedido.motivo_nao_renovacao || "—"}
                      </td>
                      <td className={cn(listagemTdCenter, "p-3")}>{criadoPor}</td>
                      <td className={cn(listagemTdCenter, "p-3")}>
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            title="Ver pedido"
                            onClick={() => setPedidoDetalhesId(pedido.id)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                            onClick={() => setPedidoReabrirId(pedido.id)}
                          >
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            Vender novamente
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pedidos.length === 0 && (
              <p className="text-center py-8 text-gray-500">Nenhum pedido não renovado registrado.</p>
            )}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span className="text-sm text-gray-600 self-center">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Arquivo de ciclos encerrados sem renovação. <strong>Vender novamente</strong> abre a proposta
            continuando o ciclo (2ª, 3ª venda…), sem zerar para 1ª.
          </p>
        </CardContent>
      </Card>

      <CriarPedidoRenovacaoDialog
        open={pedidoReabrirId != null}
        onOpenChange={(open) => {
          if (!open) setPedidoReabrirId(null);
        }}
        pedidoAnteriorId={pedidoReabrirId}
        permitirArquivoNaoRenovado
      />
      <DetalhesPedidoDialog
        open={pedidoDetalhesId != null}
        onOpenChange={(open) => {
          if (!open) setPedidoDetalhesId(null);
        }}
        pedidoId={pedidoDetalhesId}
      />
    </div>
  );
}
